import { startOfMonth, setYear, setMonth, isSameMonth, isAfter } from '@e3dc-react/shell/Libs/date'
import { Element, FixFlex, ProductData } from '../models/basic-system.model'

export interface TotalMonths {
  month: number
  year: string
}

type FixFlexYearData = FixFlex[string]

interface LatestFixFlexEntry {
  year: string
  numericYear: number
  months: number[]
  fixFlexElement: FixFlexYearData
}

/*
 * Returns all valid month keys from one fix/flex year
 */
const getSortedMonths = (fixFlexElement: FixFlexYearData): number[] => {
  return Object.keys(fixFlexElement)
    .map(month => Number.parseInt(month, 10))
    .filter(month => !Number.isNaN(month))
    .sort((a, b) => a - b)
}

/*
 * Checks if the fix/flex month is the current month or in the future
 */
const isCurrentOrFutureMonth = (year: number, month: number): boolean => {
  const currentMonth = startOfMonth(new Date())
  const cellMonth = setMonth(setYear(new Date(), year), month - 1)

  return isSameMonth(cellMonth, currentMonth) || isAfter(cellMonth, currentMonth)
}

/*
 * Converts the fix/flex element into year/month structure
 */
const getFixFlexEntries = (element: Element): LatestFixFlexEntry[] => {
  if (!element.fixFlex) {
    return []
  }

  return Object.entries(element.fixFlex)
    .map(([year, fixFlexElement]) => ({
      year,
      numericYear: Number.parseInt(year, 10),
      months: getSortedMonths(fixFlexElement),
      fixFlexElement,
    }))
    .sort((a, b) => a.numericYear - b.numericYear)
}

/*
 * Gets all unique current and future months from all elements
 */
const getCurrentAndFutureMonths = (elements: Element[]): TotalMonths[] => {
  const totalMonths: TotalMonths[] = []

  for (const element of elements) {
    for (const entry of getFixFlexEntries(element)) {
      for (const month of entry.months) {
        if (!isCurrentOrFutureMonth(entry.numericYear, month)) {
          continue
        }

        const alreadyExists = totalMonths.some(item => item.month === month && item.year === entry.year)

        if (!alreadyExists) {
          totalMonths.push({ month, year: entry.year })
        }
      }
    }
  }

  return totalMonths
}

/*
 * Removes months before the first month with available quantity
 */
const removeMonthsBeforeFirstAvailable = (elements: Element[], totalMonths: TotalMonths[]): TotalMonths[] => {
  const firstAvailableMonthIndex = totalMonths.findIndex(({ year, month }) => {
    return elements.some(element => {
      const monthData = element.fixFlex?.[year]?.[month.toString()]

      if (!monthData) {
        return false
      }

      return monthData.fixAvailable > 0 || monthData.flexAvailable > 0
    })
  })

  if (firstAvailableMonthIndex === -1) {
    return []
  }

  return totalMonths.slice(firstAvailableMonthIndex)
}

/*
 * Builds the product list for the months
 */
const formatProductList = (elements: Element[], totalMonths: TotalMonths[]): ProductData[] => {
  const productList: ProductData[] = []

  for (const [index, element] of elements.entries()) {
    for (const entry of getFixFlexEntries(element)) {
      for (const month of entry.months) {
        if (!isCurrentOrFutureMonth(entry.numericYear, month)) {
          continue
        }

        const isVisibleMonth = totalMonths.some(item => item.month === month && item.year === entry.year)

        if (!isVisibleMonth) {
          continue
        }

        const monthKey = month.toString()

        productList.push({
          productNumber: index,
          month: (month - 1).toString(),
          year: entry.year,
          monthData: entry.fixFlexElement[monthKey],
        })
      }
    }
  }

  return productList
}

/*
 * Normal configurator: Shows all current and future fix/flex months
 */
export const getTotalMonthsFromFixFlex = (elements: Element[]): TotalMonths[] => {
  return getCurrentAndFutureMonths(elements)
}

/*
 * Qntm configurator: Starts from the first current/future month where one is available
 */
export const getQntmTotalMonthsFromFixFlex = (elements: Element[]): TotalMonths[] => {
  const totalMonths = getCurrentAndFutureMonths(elements)

  return removeMonthsBeforeFirstAvailable(elements, totalMonths)
}

/*
 * Normal configurator: Reformats the list of fix/flex
 */
export const formatProductListFromFixFlex = (elements: Element[]): ProductData[] => {
  const totalMonths = getTotalMonthsFromFixFlex(elements)

  return formatProductList(elements, totalMonths)
}

/*
 * Qntm configurator: Reformats the list of fix/flex
 */
export const formatQntmProductListFromFixFlex = (elements: Element[]): ProductData[] => {
  const totalMonths = getQntmTotalMonthsFromFixFlex(elements)

  return formatProductList(elements, totalMonths)
}
